import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import apiClient from '../../services/api';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import './JoinOrder.css';

const JoinOrder: React.FC = () => {
  const navigate = useNavigate();
  const { appName } = useParams<{ appName: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAppSelector((state) => state.user);
  
  const orderId = searchParams.get('orderId');
  const tableId = searchParams.get('tableId');
  const urlPin = searchParams.get('pin'); // Get PIN from URL parameter
  
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);

  useEffect(() => {
    // Auto-populate PIN from URL if available
    if (urlPin && urlPin.length === 4) {
      const pinArray = urlPin.split('').slice(0, 4);
      setPin(pinArray);
    }
  }, [urlPin]);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      // Build return URL with all parameters
      const params = new URLSearchParams();
      if (orderId) params.set('orderId', orderId);
      if (tableId) params.set('tableId', tableId);
      if (urlPin) params.set('pin', urlPin);
      const returnUrl = `/restaurants/${appName}/join-order${params.toString() ? '?' + params.toString() : ''}`;
      navigate(`/signin?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    setCheckingAuth(false);
    // Fetch restaurant details
    fetchRestaurantDetails();
  }, [isAuthenticated, user, appName, navigate, orderId, tableId, urlPin]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await apiClient.get(`/api/restaurants/${appName}`);
      if (response.data.success && response.data.restaurant) {
        setRestaurantName(response.data.restaurant.restaurantName || appName);
      }
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      if (appName) {
        setRestaurantName(appName);
      }
    }
  };

  const handlePinChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newPin = [...pin];
    newPin[index] = value.replace(/\D/g, ''); // Only numbers
    
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const newPin = [...pin];
    
    for (let i = 0; i < 4; i++) {
      newPin[i] = pastedData[i] || '';
    }
    
    setPin(newPin);
    setError('');
    
    // Focus last filled input or first empty
    const lastFilledIndex = Math.min(pastedData.length - 1, 3);
    const nextInput = document.getElementById(`pin-${lastFilledIndex + 1}`);
    if (nextInput && lastFilledIndex < 3) {
      nextInput.focus();
    }
  };

  const handleJoinOrder = async () => {
    const pinString = pin.join('');
    
    if (pinString.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    if (!orderId) {
      setError('Order ID is missing');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post(
        `/api/restaurants/${appName}/order/join`,
        {
          orderId: parseInt(orderId),
          pin: pinString
        }
      );

      if (response.data.success) {
        // Redirect to menu with order ID
        navigate(`/restaurants/${appName}/menu?type=1&order=${orderId}`);
      } else {
        setError(response.data.message || 'Failed to join order');
      }
    } catch (error: any) {
      setError(
        error.response?.data?.message || 
        'Error joining order. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: restaurantName || appName || '', path: `/restaurants/${appName}` },
    { label: 'Join Order', path: '' }
  ];

  // Show loading if checking auth
  if (checkingAuth) {
    return (
      <PageContainer>
        <div className="join-order-loading">Loading...</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumb items={breadcrumbItems} />
      
      <div className="join-order-container">
        <div className="join-order-card">
          <h1>Join Order</h1>
          <p className="join-order-description">
            Enter the 4-digit PIN to join this shared order
          </p>

          <div className="pin-input-container">
            {pin.map((digit, index) => (
              <input
                key={index}
                id={`pin-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="pin-input"
                disabled={loading}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="join-order-button"
            onClick={handleJoinOrder}
            disabled={loading || pin.join('').length !== 4}
          >
            {loading ? 'Joining...' : 'Join Order'}
          </button>

          <p className="help-text">
            Ask the order host for the PIN or use the shared link
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default JoinOrder;

